<template>
  <div class="job-profiles-table-wrap">
    <template v-if="rows.length">
      <div class="job-profiles-desktop-table">
        <GlobalTable
          :key="table_key"
          :headers="headers"
          :data="rows"
          table_id="trust_types_table"
        >
          <template #actions="{ row }">
            <div class="job-profile-action-menu">
              <button
                class="btn btn-sm rounded-small text-xxs shadow-sm job-profile-action-menu-trigger job-profile-action-menu-view-btn"
                type="button"
                @click.stop="$emit('view-row', row)"
              >
                <span class="job-profile-action-menu-view-label">View</span>
                <i class="bi bi-chevron-right job-profile-action-menu-view-icon" aria-hidden="true"></i>
              </button>
            </div>
          </template>
        </GlobalTable>
      </div>

      <div class="job-profiles-mobile-list">
        <article
          v-for="row in paginated_mobile_rows"
          :key="get_row_key(row)"
          class="job-profile-mobile-card"
        >
          <div class="job-profile-mobile-card-main">
            <div class="job-profile-mobile-card-badge" aria-hidden="true">
              <span>{{ get_job_profile_initials(row) }}</span>
            </div>

            <div class="job-profile-mobile-card-copy">
              <h3 class="job-profile-mobile-card-title">{{ get_row_value(row, "Job Profile", "Untitled Job Profile") }}</h3>
              <p class="job-profile-mobile-card-date">{{ get_row_value(row, "Date Created", "-") }}</p>
            </div>

            <div class="job-profile-mobile-card-actions">
              <button
                class="btn btn-sm rounded-small text-xxs shadow-sm job-profile-action-menu-view-btn job-profile-mobile-card-action"
                type="button"
                @click.stop="$emit('view-row', row)"
              >
                <span class="job-profile-action-menu-view-label">View</span>
                <i class="bi bi-chevron-right job-profile-action-menu-view-icon" aria-hidden="true"></i>
              </button>
            </div>
          </div>
        </article>
      </div>

      <div v-if="show_mobile_pagination" class="job-profiles-mobile-pagination">
        <div class="job-profiles-mobile-pagination-controls">
          <button
            class="job-profiles-mobile-pagination-button job-profiles-mobile-pagination-button--nav"
            type="button"
            :disabled="mobile_current_page === 1"
            aria-label="Previous page"
            @click="go_to_mobile_page(mobile_current_page - 1)"
          >
            <i class="bi bi-chevron-left" aria-hidden="true"></i>
          </button>

          <button
            v-for="page_number in visible_mobile_page_numbers"
            :key="page_number"
            class="job-profiles-mobile-pagination-button"
            :class="{ 'job-profiles-mobile-pagination-button--active': mobile_current_page === page_number }"
            type="button"
            @click="go_to_mobile_page(page_number)"
          >
            {{ page_number }}
          </button>

          <button
            class="job-profiles-mobile-pagination-button job-profiles-mobile-pagination-button--nav"
            type="button"
            :disabled="mobile_current_page === mobile_total_pages"
            aria-label="Next page"
            @click="go_to_mobile_page(mobile_current_page + 1)"
          >
            <i class="bi bi-chevron-right" aria-hidden="true"></i>
          </button>
        </div>
      </div>
    </template>

    <div v-else class="text-center py-5">
      <i class="bi bi-inbox fs-1 text-muted d-block mb-2"></i>
      <p class="text-muted text-xxs">No job profiles created yet.</p>
    </div>
  </div>
</template>

<script>
import GlobalTable from "@/components/probaworkflows_base_components/GlobalTable/GlobalTable.vue";

export default {
  name: "ManageJobProfilesList",
  components: {
    GlobalTable,
  },
  props: {
    headers: { type: Array, required: true },
    rows: { type: Array, required: true },
    table_key: { type: String, required: true },
    mobile_items_per_page: { type: Number, default: 8 },
  },
  emits: ["view-row"],
  data() {
    const initial_state = {
      mobile_current_page: 1,
    };
    return initial_state;
  },
  computed: {
    paginated_mobile_rows() {
      const start = (this.mobile_current_page - 1) * this.mobile_items_per_page;
      const end = start + this.mobile_items_per_page;
      const current_rows = this.rows.slice(start, end);
      return current_rows;
    },
    mobile_total_pages() {
      const total_pages = Math.ceil(this.rows.length / this.mobile_items_per_page) || 1;
      return total_pages;
    },
    visible_mobile_page_numbers() {
      if (this.mobile_total_pages <= 5) {
        const page_numbers = Array.from({ length: this.mobile_total_pages }, (_, index) => index + 1);
        return page_numbers;
      }
      const start_page = this.clamp_mobile_page(this.mobile_current_page - 1, this.mobile_total_pages - 2);
      const page_numbers = [start_page, start_page + 1, start_page + 2];
      return page_numbers;
    },
    show_mobile_pagination() {
      return this.mobile_total_pages > 1;
    },
  },
  watch: {
    rows() {
      this.mobile_current_page = this.clamp_mobile_page(this.mobile_current_page);
    },
  },
  methods: {
    /** Keep the mobile page number within the available page range. */
    clamp_mobile_page(page_number, max_page = this.mobile_total_pages) {
      const numeric_page = Number(page_number) || 1;
      const safe_max_page = Math.max(max_page || 1, 1);
      const clamped_page = Math.min(Math.max(numeric_page, 1), safe_max_page);
      return clamped_page;
    },
    /** Move the mobile card list to a valid page. */
    go_to_mobile_page(page_number) {
      this.mobile_current_page = this.clamp_mobile_page(page_number);
    },
    /** Return a stable row key for desktop and mobile rendering. */
    get_row_key(row) {
      const row_key = Reflect.get(row || {}, "__row_id") || Reflect.get(row || {}, "id") || Reflect.get(row || {}, "No");
      return row_key;
    },
    /** Build a small badge label for the compact mobile row. */
    get_job_profile_initials(row) {
      const raw_title = this.get_row_value(row, "Job Profile", "Job Profile");
      const parts = String(raw_title).split(" ").filter(Boolean).slice(0, 2);
      const initials = parts.map((part) => part.charAt(0).toUpperCase()).join("") || "JP";
      return initials;
    },
    /** Read a display-safe row value without relying on dynamic template property access. */
    get_row_value(row, key, fallback = "") {
      const row_value = Reflect.get(row || {}, key);
      return row_value || fallback;
    },
  },
};
</script>

<style scoped>
.job-profiles-mobile-list {
  display: none;
}

.job-profiles-mobile-pagination {
  display: none;
}

.job-profile-action-menu-view-btn {
  min-width: 92px;
  min-height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0 14px;
  border: 1px solid #050505 !important;
  border-radius: 10px !important;
  background: #050505 !important;
  color: #fff !important;
  box-shadow: 0 8px 14px rgb(15 23 42 / 14%) !important;
  font-weight: 600;
  line-height: 1;
}

.job-profile-action-menu-view-btn:hover,
.job-profile-action-menu-view-btn:focus,
.job-profile-action-menu-view-btn:active {
  border-color: #111827 !important;
  background: #111827 !important;
  color: #fff !important;
}

.job-profile-action-menu-view-label,
.job-profile-action-menu-view-icon {
  color: inherit !important;
  line-height: 1;
}

.job-profile-action-menu-view-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  transform: translateY(0.5px);
}

@media (width <= 1199.98px) {
  .job-profiles-desktop-table {
    display: none;
  }

  .job-profiles-mobile-list {
    display: grid;
    gap: 0;
  }

  .job-profiles-mobile-pagination {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 14px;
    padding-top: 14px;
    border-top: 1px solid #e8edf2;
  }

  .job-profiles-mobile-pagination-controls {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  .job-profiles-mobile-pagination-button {
    min-width: 32px;
    min-height: 32px;
    padding: 0 10px;
    border: 1px solid #d9e1e8;
    border-radius: 999px;
    background: #fff;
    color: #111827;
    font-size: 0.76rem;
    font-weight: 700;
    line-height: 1;
  }

  .job-profiles-mobile-pagination-button:disabled {
    opacity: 0.45;
  }

  .job-profiles-mobile-pagination-button--active {
    border-color: #050505;
    background: #050505;
    color: #fff;
  }

  .job-profiles-mobile-pagination-button--nav {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
  }

  .job-profile-mobile-card {
    display: block;
    padding: 16px 0;
    border-bottom: 1px solid #e8edf2;
    background: #fff;
  }

  .job-profile-mobile-card:last-child {
    border-bottom: 0;
    padding-bottom: 0;
  }

  .job-profile-mobile-card:first-child {
    padding-top: 0;
  }

  .job-profile-mobile-card-main {
    display: grid;
    grid-template-columns: 52px minmax(0, 1fr) auto;
    gap: 12px;
    align-items: start;
  }

  .job-profile-mobile-card-badge {
    width: 52px;
    height: 52px;
    border: 1px solid #ece5bf;
    border-radius: 14px;
    display: grid;
    place-items: center;
    background: linear-gradient(180deg, #fff6cf 0%, #f9ecc0 100%);
    color: #111827;
    font-size: 0.82rem;
    font-weight: 800;
    letter-spacing: 0.04em;
  }

  .job-profile-mobile-card-copy {
    min-width: 0;
  }

  .job-profile-mobile-card-date {
    margin: 0;
  }

  .job-profile-mobile-card-date {
    margin-top: 4px;
    color: #6b7280;
    font-size: 0.8rem;
    font-weight: 500;
  }

  .job-profile-mobile-card-title {
    margin: 0;
    color: #111827;
    font-size: 0.98rem;
    font-weight: 800;
    line-height: 1.3;
  }

  .job-profile-mobile-card-actions {
    display: flex;
    align-items: flex-start;
    min-width: 92px;
  }

  .job-profile-mobile-card-action {
    width: auto;
    min-height: 34px;
    padding-inline: 14px;
    border-radius: 999px !important;
    box-shadow: none !important;
  }

  .job-profile-mobile-card-action,
  .job-profile-mobile-card-action:hover,
  .job-profile-mobile-card-action:focus {
    background: #050505 !important;
    color: #fff !important;
  }

  .job-profile-mobile-card-action .job-profile-action-menu-view-label,
  .job-profile-mobile-card-action .job-profile-action-menu-view-icon {
    color: inherit !important;
  }

  .job-profile-mobile-card {
    box-shadow: none;
    border-radius: 0;
  }

  .job-profile-mobile-card-meta,
  .job-profile-mobile-card-meta-row {
    display: none;
  }

  .job-profile-mobile-card-action {
    min-width: 92px;
  }

  .job-profile-mobile-card {
    align-content: start;
  }

  .job-profile-mobile-card-copy {
    overflow: hidden;
  }

  .job-profile-mobile-card-main {
    min-width: 0;
  }

  .job-profile-mobile-card-badge span {
    display: inline-block;
    transform: translateY(0.5px);
  }

  .job-profile-mobile-card-inline-actions {
    display: none;
  }

  .job-profile-mobile-card-title {
    padding-right: 4px;
  }

  .job-profile-mobile-card-action {
    letter-spacing: 0;
  }

  .job-profile-mobile-card-badge {
    box-shadow: inset 0 1px 0 rgb(255 255 255 / 55%);
  }

  .job-profile-mobile-card {
    min-width: 0;
  }

  .job-profile-mobile-card-copy {
    display: block;
  }

  .job-profile-mobile-card-title {
    min-width: 0;
  }

  .job-profile-mobile-card-date {
    line-height: 1.35;
  }

  .job-profile-mobile-card-title,
  .job-profile-mobile-card-date {
    overflow-wrap: anywhere;
  }

  .job-profile-mobile-card-badge {
    background: #fff;
    background: linear-gradient(180deg, #fff6cf 0%, #f9ecc0 100%);
  }
}

@media (768px <= width <= 1199.98px) {
  .job-profiles-mobile-list {
    gap: 2px;
  }

  .job-profile-mobile-card {
    padding: 18px 0;
  }

  .job-profile-mobile-card-main {
    grid-template-columns: 56px minmax(0, 1fr) auto;
    gap: 14px;
  }

  .job-profile-mobile-card-badge {
    width: 56px;
    height: 56px;
  }

  .job-profile-mobile-card-title {
    font-size: 1.02rem;
  }

  .job-profile-mobile-card-date {
    font-size: 0.84rem;
  }

  .job-profile-mobile-card-actions {
    min-width: 98px;
  }
}

@media (width <= 575.98px) {
  .job-profiles-mobile-pagination {
    flex-direction: column;
    align-items: center;
    gap: 10px;
  }

  .job-profiles-mobile-pagination-controls {
    flex-wrap: wrap;
    justify-content: center;
  }

  .job-profile-mobile-card-main {
    grid-template-columns: 48px minmax(0, 1fr);
    gap: 10px;
  }

  .job-profile-mobile-card-actions {
    grid-column: 2;
    align-items: flex-start;
    margin-top: 2px;
  }

  .job-profile-mobile-card-badge {
    width: 48px;
    height: 48px;
    border-radius: 12px;
  }
}
</style>
